// app/shared/components/layout/HTFSection.tsx
import React from "react";

import { cn } from "@/lib/utils";

interface HTFSectionProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  // Corrected 'as' prop type:
  as?: React.ElementType; // Use React.ElementType for dynamic components
  bgColorVariant?:
    | "beige"
    | "beigeDark"
    | "surfaceLight"
    | "surfaceDark"
    | "transparent"
    | "teal"
    | "tealDark"
    | "orange"
    | "orangeDark"
    | "purple"
    | "purpleDark"
    | "pink"
    | "pinkDark"
    | "green"
    | "greenDark"
    | "lime"
    | "indigo"
    | "black";
  paddingY?: "sm" | "md" | "lg" | "xl";
  container?: boolean;
  /** Max width variant: 'default' uses container, '8xl'/'9xl' uses max-w for wider layouts, 'full' removes max-width */
  maxWidth?: "default" | "8xl" | "9xl" | "full";
}

export function HTFSection({
  children,
  className,
  id,
  as: Component = "section", // Default to 'section', assign to a capitalized variable
  bgColorVariant = "transparent",
  paddingY = "lg",
  container = true,
  maxWidth = "default",
}: HTFSectionProps) {
  const bgClasses = {
    beige: "bg-beige",
    beigeDark: "bg-beige-dark",
    surfaceLight: "bg-surface-light",
    surfaceDark: "bg-surface-dark",
    teal: "bg-teal",
    tealDark: "bg-teal-dark",
    orange: "bg-orange",
    orangeDark: "bg-orange-dark",
    purple: "bg-purple",
    purpleDark: "bg-purple-dark",
    pink: "bg-pink",
    pinkDark: "bg-pink-dark",
    green: "bg-green",
    greenDark: "bg-green-dark",
    lime: "bg-lime",
    indigo: "bg-indigo",
    black: "bg-black",
    transparent: "",
  };

  const pyClasses = {
    sm: "py-8 md:py-12",
    md: "py-12 md:py-16",
    lg: "py-16 md:py-24",
    xl: "py-20 md:py-28 lg:py-32",
  };

  // Default text contrast: only `black` is a true dark band.
  // `surfaceDark` is a light beige token (#e8e4d8) — light text on it is unreadable.
  const textColorClass =
    bgColorVariant === "black" ? "text-white" : "text-black";

  // For 8xl/9xl/full, padding goes on outer section (like hero), inner just has max-w
  const useOuterPadding =
    maxWidth === "8xl" || maxWidth === "9xl" || maxWidth === "full";

  return (
    <Component // Use the capitalized variable for the dynamic component
      id={id}
      className={cn(
        "w-full",
        bgClasses[bgColorVariant],
        pyClasses[paddingY],
        textColorClass, // Apply base text color
        useOuterPadding && "px-6 md:px-12 lg:px-16",
        className,
      )}
    >
      {container ? (
        <div
          className={cn(
            maxWidth === "default" && "container px-4 md:px-6",
            maxWidth === "8xl" && "w-full max-w-8xl mx-auto",
            maxWidth === "9xl" && "w-full max-w-[1800px] mx-auto",
            maxWidth === "full" && "w-full mx-auto",
          )}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </Component>
  );
}
